import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import config from '../config';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WarningIcon from '@mui/icons-material/Warning';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BookIcon from '@mui/icons-material/Book';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { getExpiryBannerDaysRemaining } from '../utils/subscriptionExpiry';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppSupport from './shared/WhatsAppSupport';
import { trackMemberActivity } from '../utils/memberActivity';
import { fetchWithUserAuth } from '../utils/userApi';

// Fallback cover images (used only if database doesn't have cover URLs)
const FALLBACK_MAGAZINE_COVERS = {
  beginner: {
    current: 'https://dp9h3awfnhccl.cloudfront.net/magazines/thisweek_beginner.jpeg',
    week1: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week1_beginner.jpeg',
    week2: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week2_beginner.jpeg'
  },
  junior: {
    current: 'https://dp9h3awfnhccl.cloudfront.net/magazines/thisweek_junior.jpeg',
    week1: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week1_junior.jpeg',
    week2: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week2_junior.jpeg'
  },
  senior: {
    current: 'https://dp9h3awfnhccl.cloudfront.net/magazines/thisweek_senior.jpeg',
    week1: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week1_senior.jpeg',
    week2: 'https://dp9h3awfnhccl.cloudfront.net/magazines/week2_senior.jpeg'
  }
};

const Magazine = () => {
  const navigate = useNavigate();
  const { userData, logout, refreshUserData } = useUserAuth();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [magazines, setMagazines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [previousIssues, setPreviousIssues] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // Refresh user data on mount to get latest edition and other updates
  useEffect(() => {
    const refreshData = async () => {
      if (refreshUserData) {
        await refreshUserData();
      }
    };
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch magazines from API
  useEffect(() => {
    const fetchMagazines = async () => {
      try {
        setLoading(true);
        const apiUrl = config.getApiUrl();
        const cacheBuster = `?t=${new Date().getTime()}`;
        const data = await fetchWithUserAuth(`${apiUrl}/api/magazines/public${cacheBuster}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        console.log('📚 Magazine data fetched:', data);
        setMagazines(data);
        
        const edition = userData?.edition?.toLowerCase();
        console.log('👤 User edition:', edition);
        
        if (edition && data[edition]) {
          const editionData = data[edition];
          const fallbackCovers = FALLBACK_MAGAZINE_COVERS[edition];
          console.log('📖 Edition data:', editionData);
          console.log('🎨 Fallback covers:', fallbackCovers);
          
          if (editionData.current) {
            const currentIssueData = {
              id: 'current',
              title: 'Current Issue',
              // Use cover from database if available, otherwise use fallback
              image: editionData.current.cover || fallbackCovers.current,
              docUrl: editionData.current.link
            };
            console.log('✅ Current issue data:', currentIssueData);
            setCurrentIssue(currentIssueData);
          }
          
          if (editionData.previous && editionData.previous.length > 0) {
            // Show ALL previous issues from database in REVERSE order (newest first)
            const prevIssues = [...editionData.previous].reverse().map((issue, index) => ({
              id: `issue${index + 1}`,
              title: issue.title || `Volume ${index + 1}`,
              // Use cover1 from database if available, otherwise use fallback
              image: issue.cover1 || (index === 0 ? fallbackCovers.week1 : fallbackCovers.week2),
              docUrl: issue.link1
            }));
            console.log('📰 Previous issues (reversed for display):', prevIssues);
            setPreviousIssues(prevIssues);
          } else {
            console.log('⚠️ No previous issues found in database');
          }
        } else {
          console.log('❌ Edition not found in data or no edition set');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching magazines:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (userData?.edition) {
      fetchMagazines();
    } else {
      setLoading(false);
    }
  }, [userData]);

  useEffect(() => {
    setDaysRemaining(getExpiryBannerDaysRemaining(userData?.subscription));
  }, [userData]);

  const handleLogout = () => {
    logout();
    navigate('/user-login');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReadNow = (issueId) => {
    let issue = null;
    if (issueId === currentIssue?.id) {
      issue = currentIssue;
    } else {
      issue = previousIssues.find(i => i.id === issueId);
    }
    
    if (issue && issue.docUrl) {
      trackMemberActivity('read_now');
      let docUrl = issue.docUrl;
      if (userData?.email) {
        const separator = docUrl.includes('?') ? '&' : '?';
        docUrl = `${docUrl}${separator}email=${encodeURIComponent(userData.email)}`;
      }
      
      window.open(docUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)'
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)'
        }}
      >
        <Toolbar>
          <IconButton
            onClick={() => navigate('/user-dashboard')}
            sx={{
              color: '#FF6B35',
              mr: 1,
              '&:hover': {
                background: 'rgba(255, 107, 53, 0.1)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <MenuBookIcon sx={{ color: '#FF6B35', mr: 2, fontSize: 32 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            YoungPresso
          </Typography>
          <Button
            onClick={handleMenuOpen}
            startIcon={<AccountCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            sx={{
              color: 'white',
              mr: { xs: 0.5, sm: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '4px 8px', sm: '6px 12px' },
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '120px', sm: 'none' },
              '&:hover': {
                background: 'rgba(255, 107, 53, 0.2)'
              }
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Hi {userData?.name || 'User'}!
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* User Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 26, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 107, 53, 0.3)',
            borderRadius: 2,
            mt: 1,
            minWidth: 300
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: '#FFB74D', fontWeight: 600, mb: 0.5 }}>
            User Profile
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {userData?.name || 'User'}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            {userData?.subscription?.status?.toLowerCase() === 'cancelled' ? (
              <CancelIcon sx={{ color: '#f44336' }} />
            ) : (
              <CheckCircleIcon sx={{ color: '#4CAF50' }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscription Status</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>{userData?.subscription?.status || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <EmailIcon sx={{ color: '#06AED5' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Email</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{userData?.email || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <PhoneIcon sx={{ color: '#FFB74D' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Phone</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{userData?.phone || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <BookIcon sx={{ color: '#FF6B35' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscribed Edition</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>{userData?.edition || 'N/A'}</Typography>}
          />
        </MenuItem>

        <MenuItem sx={{ py: 1.5, '&:hover': { background: 'rgba(255, 107, 53, 0.1)' } }}>
          <ListItemIcon>
            <SubscriptionsIcon sx={{ color: '#9C27B0' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Subscribed Plan</Typography>}
            secondary={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>{userData?.period || 'N/A'}</Typography>}
          />
        </MenuItem>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
        
        <MenuItem 
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
          sx={{ 
            py: 1.5, 
            '&:hover': { 
              background: 'rgba(255, 107, 53, 0.2)' 
            } 
          }}
        >
          <ListItemIcon>
            <LogoutIcon sx={{ color: '#FF6B35' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>Sign Out</Typography>}
          />
        </MenuItem>
      </Menu>

      {/* Countdown Ticker for Cancelled Subscriptions */}
      {daysRemaining != null && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(255, 107, 53, 0.05) 100%)',
            borderBottom: '3px solid #FF6B35',
            backdropFilter: 'blur(10px)',
            py: { xs: 2, sm: 2.5, md: 3 },
            px: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2, md: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#FF6B35',
                  mb: 1,
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <WarningIcon
                  sx={{
                    fontSize: { xs: 28, sm: 32, md: 36 },
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }}
                />
                Uh oh! ⏰ Your access expires in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
                }}
              >
                📚 Don't let your subscription lapse! Subscribe again and continue the learning journey with YoungPresso.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/pricing')}
              sx={{
                background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                color: 'white',
                fontWeight: 700,
                px: { xs: 2.5, sm: 3, md: 4 },
                py: { xs: 1, sm: 1.2, md: 1.5 },
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)'
                },
                transition: 'all 0.3s ease',
                flexShrink: 0
              }}
            >
              Subscribe Again
            </Button>
          </Box>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 4 } }}>
        {/* Edition Display */}
        {userData?.edition && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 2, sm: 3, md: 4 },
              textAlign: 'center',
              fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
              textTransform: 'capitalize'
            }}
          >
            {userData.edition} Edition
          </Typography>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress sx={{ color: '#FF6B35' }} size={60} />
          </Box>
        )}

        {/* Error or No Edition State */}
        {!loading && !userData?.edition && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 4,
              background: 'rgba(255, 152, 0, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              '& .MuiAlert-icon': {
                color: '#FFB74D'
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Edition Not Updated
            </Typography>
            <Typography variant="body2">
              Your edition information is not available yet. Please contact support to update your subscription details.
            </Typography>
          </Alert>
        )}

        {/* Magazines Display */}
        {!loading && userData?.edition && currentIssue && (
          <>
            {/* Current Issue Section */}
            <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 2, sm: 2.5, md: 3 },
              textAlign: 'center',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            Current Issue
          </Typography>

            <Paper
              elevation={10}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 2, sm: 3, md: 3 },
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '2px solid',
                borderColor: 'transparent',
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)), linear-gradient(45deg, #FF6B35, #06AED5)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: { xs: 'translateY(-4px)', sm: 'translateY(-8px)' },
                  boxShadow: '0 12px 40px rgba(255, 107, 53, 0.3)'
                }
              }}
            >
              <Box
                component="img"
                src={currentIssue.image}
                alt="Current Issue"
                onError={(e) => {
                  console.error('Failed to load current issue image:', currentIssue.image);
                  e.target.style.display = 'none';
                }}
                sx={{
                  width: '100%',
                  maxHeight: { xs: 300, sm: 400, md: 600 },
                  objectFit: 'contain',
                  display: 'block',
                  background: '#000'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)',
                  p: { xs: 1.5, sm: 2, md: 3 },
                  textAlign: 'center'
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleReadNow(currentIssue.id)}
                  sx={{
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                    px: { xs: 2.5, sm: 3, md: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 20px rgba(255, 107, 53, 0.5)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Read Now
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Previous Issues Section */}
          {previousIssues.length > 0 && (
            <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #06AED5 30%, #4FC3F7 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 2, sm: 2.5, md: 3 },
              textAlign: 'center',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            Previous Issues
          </Typography>

          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 4 }}
            sx={{
              justifyContent: 'center'
            }}
          >
              {previousIssues.map((issue, index) => (
                <Grid
                  item
                  key={issue.id}
                  xs={12}
                  sm={6}
                  md={6}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#4FC3F7',
                        fontWeight: 700,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                        textAlign: 'left',
                        mb: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                      }}
                    >
                      {issue.title}
                    </Typography>

                    <Paper
                      elevation={10}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: { xs: 2, sm: 3, md: 3 },
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid',
                        borderColor: 'transparent',
                        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)), linear-gradient(45deg, #06AED5, #4FC3F7)',
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                        transition: 'all 0.3s ease',
                        animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`,
                        '@keyframes fadeInUp': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(30px)'
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0)'
                          }
                        },
                        '&:hover': {
                          transform: { xs: 'translateY(-4px)', sm: 'translateY(-8px)' },
                          boxShadow: '0 12px 40px rgba(6, 174, 213, 0.3)'
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={issue.image}
                        alt={issue.title}
                        sx={{
                          width: '100%',
                          maxHeight: { xs: 300, sm: 400, md: 500 },
                          objectFit: 'contain',
                          display: 'block',
                          background: '#000'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)',
                          p: { xs: 1.5, sm: 2, md: 3 },
                          textAlign: 'center'
                        }}
                      >
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => handleReadNow(issue.id)}
                          sx={{
                            py: { xs: 1, sm: 1.2, md: 1.5 },
                            px: { xs: 2.5, sm: 3, md: 4 },
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            fontWeight: 700,
                            background: 'linear-gradient(45deg, #06AED5 30%, #4FC3F7 70%)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #4FC3F7 30%, #81D4FA 70%)',
                              transform: 'scale(1.05)',
                              boxShadow: '0 6px 20px rgba(6, 174, 213, 0.5)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Read Now
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
          )}
        </>
        )}

        <WhatsAppSupport
          title="Trouble viewing the magazine?"
          description="Not able to read the magazine, or your edition looks incorrect? Reach us on WhatsApp and we will fix it."
          message="I am having trouble viewing magazine / My Edition is not correct"
          sx={{ mt: 4, mb: 2 }}
        />
      </Container>

    </Box>
  );
};

export default Magazine;
